import {classes} from './PaymentAndPlaceOrderStep.st.css';
import {PaymentsWidget} from '@wix/cashier-payments-widget/lazy';
import {getCashierMandatoryFieldsOverrides, getHiddenFieldsByCheckout} from '../../../../../domain/utils/cashier.utils';
import React, {useEffect} from 'react';
import {NoOnlinePaymentsNotification} from './NoOnlinePaymentsNotification/NoOnlinePaymentsNotification';
import {useControllerProps} from '../../../Widget/ControllerContext';
import {usePaymentsApi} from '../../../../../common/components/WithPaymentsApi/WithPaymentsApi';
import {StartPayment} from '../../../../../domain/utils/analytics.utils';
import {getPaymentsWidgetBIData} from '../../../../../domain/utils/bi.util';
import {StepState} from '../../../../../types/checkoutApp.types';
import {getCountryFromLocaleDataset} from '../../../../../domain/utils/localeDataset.util';
import {useBillingData} from './WithBillingData';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {SPECS} from '../../../../../common/constants';
import {type MFieldName} from '@wix/cashier-payments-widget/src/types/MandatoryFields';

export enum PaymentWidgetWrapperDataHooks {
  root = 'PaymentWidgetWrapperDataHooks.root',
  emptyRoot = 'PaymentWidgetWrapperDataHooks.emptyRoot',
}

export const PaymentWidgetWrapper = ({
  stepState,
  paymentWidgetRef,
}: {
  stepState: StepState;
  paymentWidgetRef?: React.RefObject<HTMLDivElement>;
}) => {
  const {
    paymentStore: {cashierWidgetProps, setCashierMandatoryFields},
    navigationStore: {trackEvent},
    checkoutStore: {cart, hasNoPaymentMethods, setHasNoPaymentMethods},
  } = useControllerProps();

  const {setPaymentsApi, setActivePaymentId} = usePaymentsApi();
  const {contactCountry} = useBillingData();
  const {experiments} = useExperiments();

  useEffect(
    () => {
      if (stepState === StepState.OPEN) {
        trackEvent(...StartPayment);
      }
    },
    /* eslint-disable react-hooks/exhaustive-deps */ [stepState]
  );

  const wrapperClass: {[state: string]: string} = {
    [StepState.EMPTY]: classes.paymentEmpty,
    [StepState.OPEN]: classes.paymentOpen,
  };

  /* istanbul ignore if */
  if (!cashierWidgetProps) {
    return <div className={wrapperClass[stepState]} data-hook={PaymentWidgetWrapperDataHooks.emptyRoot}></div>;
  }

  const handleMandatoryFieldsChanged = (mandatoryFields: MFieldName[]) => {
    if (experiments.enabled(SPECS.SupportDynamicBillingFieldsCheckout)) {
      setCashierMandatoryFields(getCashierMandatoryFieldsOverrides(mandatoryFields));
    }
  };

  const handlePaymentMethodsLoaded = (paymentMethods: unknown[]) => {
    if (experiments.enabled(SPECS.HandleCheckoutErrorStates)) {
      setHasNoPaymentMethods(paymentMethods.length === 0);
    }
  };

  const isWidgetHidden = stepState === StepState.EMPTY;
  const isWidgetVisible = !isWidgetHidden && !hasNoPaymentMethods;

  const {configuration, ...cashierProps} = cashierWidgetProps;
  const country3 = contactCountry ? getCountryFromLocaleDataset(contactCountry)?.key : undefined;
  return (
    <>
      <div
        className={hasNoPaymentMethods ? classes.paymentEmpty : wrapperClass[stepState]}
        data-hook={PaymentWidgetWrapperDataHooks.root}
        ref={paymentWidgetRef}>
        <PaymentsWidget
          {...cashierProps}
          biData={getPaymentsWidgetBIData(cart)}
          configuration={{...configuration, country: country3}}
          isWidgetVisible={experiments.enabled(SPECS.HandleCheckoutErrorStates) ? isWidgetVisible : !isWidgetHidden}
          isFullWidth={true}
          onApiInit={setPaymentsApi}
          onMandatoryFieldsChanged={handleMandatoryFieldsChanged}
          onPaymentMethodsLoaded={handlePaymentMethodsLoaded}
          paymentMethodChanged={setActivePaymentId}
          paymentResult={/* istanbul ignore next */ () => null}
          mandatoryFieldsHidden={getHiddenFieldsByCheckout(cart, contactCountry)}
        />
      </div>
      {hasNoPaymentMethods && (
        <div className={wrapperClass[stepState]}>
          <NoOnlinePaymentsNotification billingCountry={country3} />
        </div>
      )}
    </>
  );
};
